import { createFileRoute } from '@tanstack/react-router';
import { useIxcAssetsV1AssetQueryServiceAssetGet } from '@interxfi/api-client/generated/hooks/ixc-assets-v1-asset-query-service-asset-get';
import { DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE, PageLayout } from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { PageTabRail } from '../../../../../../../components/page-tab-rail/page-tab-rail';
import { type PortalTabbedNavigationTab, TabbedNavigationGroup } from '../../../../../../../components/router/router-tabbed-navigation';
import { getAssetFilingsPath } from '../../../../../../../features/asset/asset-routing';
import { getAssetSymbol } from '../../../../../../../features/asset/utils';
import { MarketDataFilingsTable, type MarketDataFilingsTableHeaderProps } from '../../../../../../../features/market-data/market-data-filings-table';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../util/routing/meta';

export const Route = createFileRoute('/_app/_clearing-core/asset/_asset/$assetId/_detail/filings')({
  staticData: {
    tab: {
      group: TabbedNavigationGroup.AssetMarketData,
      label: 'Filings',
      order: 1,
    },
  } as const satisfies PortalTabbedNavigationTab<TabbedNavigationGroup.AssetMarketData>,
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `asset-${params.assetId}-filings`,
        label: 'Market Data',
        to: getAssetFilingsPath(params.assetId),
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('Filings'),
  component: AssetFilings,
});

function TableHeader({ filter }: MarketDataFilingsTableHeaderProps) {
  return <PageTabRail actions={filter} group={TabbedNavigationGroup.AssetMarketData} />;
}

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load asset', 'view this asset');

function AssetFilings() {
  const { assetId } = Route.useParams();
  const { data, error, isPending } = useIxcAssetsV1AssetQueryServiceAssetGet({ assetId });
  useErrorHandler(error, errorMessageGetter);
  const symbol = getAssetSymbol(data?.asset?.data?.type);

  return (
    <PageLayout maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section noBorder>
        <MarketDataFilingsTable HeaderActions={TableHeader} isPending={Boolean(isPending || error)} symbol={symbol} />
      </PageLayout.Section>
    </PageLayout>
  );
}
